import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { consumersApi } from '../api/consumersApi';
import type { ConsumerFilters, ConsumerFormValues } from '../types/consumer';

export function useConsumersQuery(
  filters: ConsumerFilters,
  page: number,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ['gateway', 'consumers', filters, page],
    queryFn: () => consumersApi.list({ ...filters, page, size: 20, sort: 'createdAt,desc' }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    refetchInterval: 30_000,
    enabled: options?.enabled ?? true,
    retry: false,
  });
}

export function useConsumerQuery(id: string | null) {
  return useQuery({
    queryKey: ['gateway', 'consumer', id],
    queryFn: () => consumersApi.get(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useCreateConsumer(onSuccess?: (username: string) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ConsumerFormValues) => consumersApi.create(body),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['gateway', 'consumers'] });
      onSuccess?.(created.username);
    },
  });
}

export function useReplaceConsumer(id: string, onSuccess?: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ConsumerFormValues) => consumersApi.replace(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gateway', 'consumers'] });
      qc.invalidateQueries({ queryKey: ['gateway', 'consumer', id] });
      onSuccess?.();
    },
  });
}

export function useDeleteConsumer(onSuccess?: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => consumersApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gateway', 'consumers'] });
      onSuccess?.();
    },
  });
}
